import { BUILD_ENVIRONMENT_IDENTIFIER } from './build-environment-identifier';
import { environmentConfigs } from './environment-config';

const HOSTNAME_ENVIRONMENT_MAP: Record<string, string> = {
  'dev.tryglamera.com': 'dev',
  'dev.localhost': 'dev',
  'stage.tryglamera.com': 'stage',
  'stage.localhost': 'stage',
  'pro.tryglamera.com': 'live',
  'pro.localhost': 'live',
  'sys.tryglamera.com': 'live',
  'sys.localhost': 'live',
  'app.tryglamera.com': 'live',
  'app.localhost': 'live',
  'prodump.tryglamera.com': 'prodump',
  'prodump.localhost': 'prodump',
  'live.tryglamera.com': 'live',
  'live.localhost': 'live',
};

/**
 * Maps deployment hostname to AppDomain config key.
 * Separate from API environment: pro/app/sys use live APIs but keep their own AppDomain.
 */
const HOSTNAME_APP_DOMAIN_KEY_MAP: Record<string, string> = {
  'dev.tryglamera.com': 'dev',
  'dev.localhost': 'dev',
  'stage.tryglamera.com': 'stage',
  'stage.localhost': 'stage',
  'pro.tryglamera.com': 'pro',
  'pro.localhost': 'pro',
  'sys.tryglamera.com': 'sys',
  'sys.localhost': 'sys',
  'app.tryglamera.com': 'app',
  'app.localhost': 'app',
  'prodump.tryglamera.com': 'prodump',
  'prodump.localhost': 'prodump',
  'live.tryglamera.com': 'live',
  'live.localhost': 'live',
};

/** Environments where booking/staging URLs must not be indexed (keep in sync with environment-config blockSearchIndexing). */
export const SEARCH_INDEX_BLOCKED_ENVIRONMENTS = new Set(['dev', 'stage', 'prodump']);

/**
 * Determines the environment key: build config, window.ENVIRONMENT_IDENTIFIER, localStorage, URL ?env=, hostname map, else dev.
 */
export function getEnvironmentIdentifier(): string {
  if (BUILD_ENVIRONMENT_IDENTIFIER) {
    return BUILD_ENVIRONMENT_IDENTIFIER;
  }

  const envVar = (window as Window & { ENVIRONMENT_IDENTIFIER?: string }).ENVIRONMENT_IDENTIFIER;
  if (envVar) {
    return envVar;
  }

  const localStorageEnv = localStorage.getItem('environment_identifier');
  if (localStorageEnv) {
    return localStorageEnv;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const urlEnv = urlParams.get('env');
  if (urlEnv) {
    return urlEnv;
  }

  const hostname = window.location.hostname;

  for (const key in HOSTNAME_ENVIRONMENT_MAP) {
    if (hostname.includes(key)) {
      return HOSTNAME_ENVIRONMENT_MAP[key];
    }
  }

  return 'dev';
}

/**
 * Resolves AppDomain from the current hostname so redirects match the deployment
 * (e.g. pro.tryglamera.com → pro.glamera.com) even when API config is shared as live.
 */
export function getAppDomain(fallbackIdentifier: string = getEnvironmentIdentifier()): string {
  const hostname = window.location.hostname;

  for (const key in HOSTNAME_APP_DOMAIN_KEY_MAP) {
    if (hostname.includes(key)) {
      const appDomainKey = HOSTNAME_APP_DOMAIN_KEY_MAP[key];
      return environmentConfigs[appDomainKey]?.AppDomain
        ?? environmentConfigs[fallbackIdentifier]?.AppDomain
        ?? 'dev.glamera.com';
    }
  }

  return environmentConfigs[fallbackIdentifier]?.AppDomain ?? 'dev.glamera.com';
}

export function shouldBlockSearchIndexing(identifier: string = getEnvironmentIdentifier()): boolean {
  return SEARCH_INDEX_BLOCKED_ENVIRONMENTS.has(identifier);
}
